import { useContext, useEffect, useState } from "react";
import {
  getTransactions,
  createTransaction,
  updateTransaction,
  deleteTransaction,
} from "../services/configurations/transactions";
import { Transaction, TransactionProperty } from "../interfaces";
import { AppContext } from "../context";

type DataResponseType = "DATA" | "ID";
type ActionType = "CREATE" | "UPDATE" | "DELETE";

interface TransactionWithProperties extends Transaction {
  transactionproperties?: TransactionProperty[];
}

const transactionKeysProperties = [
  "name",
  "systemfunction",
  "labelkey",
  "wildcardcode",
  "authorizationflowname",
  "type",
  "beneficiaryregistrationtype",
  "fieldorder",
  "isbatch",
  "status",
  "releaseversion",
];

const handleActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createTransaction,
        fnMsg: t("Transactions.transactionApiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateTransaction,
        fnMsg: t("Transactions.transactionApiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteTransaction,
        fnMsg: t("Transactions.transactionApiDeleteLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useTransactions = (type: DataResponseType) => {
  const {
    t,
    toast,
    appState: { selectedApp, selectedTransaction },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<TransactionWithProperties[] | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || "0";

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const transactionAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handleActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (type === "ID" && appid) {
      (async function () {
        setAppState((prev) => ({
          ...prev,
          isLoading: true,
        }));
        await Promise.resolve(getTransactions(appid, selectedTransaction?.id))
          .then((response) => {
            const transaction: TransactionWithProperties[] = [response?.data];
            const extraProperties = transactionKeysProperties.map((key) => {
              return {
                key,
                value:
                  key === "status"
                    ? `${(transaction[0] as any)[key] === "ACTIVE"}`
                    : `${(transaction[0] as any)[key]}`,
                type: ["isbatch", "status"].includes(key) ? "boolean" : "text",
                label: ``
              };
            });
            (transaction[0]?.transactionproperties || [])
            .unshift(...extraProperties!);
            setData(transaction);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
        setAppState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      })();
    }
  }, [t, appid, toast, type, selectedTransaction?.id, setAppState]);

  useEffect(() => {
    if (type === "DATA" && appid) {
      (async function () {
        await Promise.resolve(getTransactions(appid))
          .then((response) => {
            const transactions: Transaction[] = response?.data;
            setData(transactions);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, reload, type]);

  return {
    data,
    transactionAction,
    reloadFn,
  };
};
